"use client";

import { setNotification } from "@/app/components/notifications";
import { capFirst } from "@/lib/strapi/formatters";
import { handleResponseError } from "@/lib/strapi/strapiApi";
import { passwordRequirements } from "@/lib/strapi/verification";
import { scrollToTop } from "@/lib/utils/helpers";
import { useSession } from "next-auth/react";
import { DEV_LOGIN_DENIED_MESSAGE } from "@/enums/auth";
import { UserCoreRegistrationFields } from "@/types/next-auth";
import {
  Anchor,
  Box,
  Button,
  Center,
  SegmentedControl,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  ContactPageRounded,
  HourglassBottomRounded,
  WhatshotRounded,
} from "@mui/icons-material";
import { Stack } from "@mui/material";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useAuthStyles } from "../../(auth)/components/AuthContentWrapper";
import FieldCheckboxValue from "../../profile/components/inputs/FieldCheckboxValue";
import FieldLabel from "../../profile/components/inputs/FieldLabel";
import FieldPasswordValue from "../../profile/components/inputs/FieldPasswordValue";
import FieldTextValue from "../../profile/components/inputs/FieldTextValue";
import {
  CreateAccountFormValues,
  emailRequirements,
  notBlankTextRequirements,
  SelectedAccountType,
} from "../types";
import AccountTypePreview, {
  getColourForSelectedAccountType,
} from "./AccountTypePreview";
import FeatureResourcePreviewAccordion from "./FeatureResourcePreviewAccordion";
import classes from "./CreateAccountForm.module.css";

export default function CreateAccountForm() {
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedAccountType, setSelectedAccountType] =
    useState<SelectedAccountType>("Basic");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { data: session } = useSession();
  const { classes: authClasses } = useAuthStyles();

  useEffect(() => {
    if (session?.user) {
      router.push("/register/membership?accountType=Trial");
    }
  }, []);

  const form = useForm<CreateAccountFormValues>({
    initialValues: {
      selectedAccountType: selectedAccountType,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      termsandprivacy: false,
    },
    validate: (values) => {
      return {
        firstName:
          /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð,.'-]+$/.test(
            values.firstName
          )
            ? null
            : "Enter a valid first name without spaces",
        lastName: notBlankTextRequirements.test(values.lastName)
          ? null
          : "Enter a valid last name",
        email: emailRequirements.test(values.email) ? null : "Invalid email",
        password: passwordRequirements.every((req) =>
          req.re.test(values.password)
        )
          ? null
          : "Password must contain the required properties",
        confirmPassword:
          values.confirmPassword === values.password
            ? null
            : "Passwords do not match",
        termsandprivacy:
          values.termsandprivacy === true
            ? null
            : "Agree to our terms and privacy policy",
      };
    },
    validateInputOnChange: [
      "firstName",
      "lastName",
      "email",
      "password",
      "confirmPassword",
    ],
  });
  useEffect(() => {
    if (!form.values.confirmPassword) {
      form.clearFieldError("confirmPassword");
      return;
    }
    form.validateField("confirmPassword");
  }, [form.values.password]);

  async function createUserAccount() {
    try {
      setLoading(true);
      if (!executeRecaptcha) {
        throw new Error(
          "Our system was not able to verify your human status. Please check you are connected to the internet. If so, try again now!"
        );
      }
      const captchaToken = await executeRecaptcha("RegisterSubmit");
      const res = await fetch("/api/strapi/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${capFirst(form.values.firstName)} ${capFirst(
            form.values.lastName
          )}`,
          username: form.values.email,
          email: form.values.email,
          password: form.values.password,
          captchaToken,
        } as UserCoreRegistrationFields),
      });
      const response = await res.json();
      if (!res.ok) {
        throw new Error(handleResponseError(response));
      }
      const captchaToken2 = await executeRecaptcha("LoginSubmit");
      const res2 = await signIn("credentials", {
        redirect: false,
        email: response.email,
        password: form.values.password,
        captchaToken: captchaToken2,
      });
      if (res2?.error) {
        throw new Error(
          res2.error === DEV_LOGIN_DENIED_MESSAGE
            ? DEV_LOGIN_DENIED_MESSAGE
            : "Your account was created, but we could not log you in. Please try to login with the details you just entered!"
        );
      }
      return router.push(
        selectedAccountType === "Basic"
          ? "/profile"
          : `/register/membership?accountType=${selectedAccountType}`
      );
    } catch (err: any) {
      setNotification("Registration", false, err.message, "error");
    } finally {
      scrollToTop();
      setLoading(false);
    }
  }

  return (
    <Box className={classes.registerInterfaceWrapper}>
      <Stack className={authClasses.contentWrapper}>
        <div className={authClasses.borderWrapper}>
          <Box className={authClasses.mainContent}>
            <Box className={classes.accountTypeSelector}>
              <FieldLabel label="Account Type" />
              <SegmentedControl
                w="100%"
                size="xs"
                color={"dark"}
                value={selectedAccountType}
                onChange={(value) =>
                  setSelectedAccountType(value as SelectedAccountType)
                }
                data={options.map((op) => {
                  return {
                    value: op.value,
                    label: (
                      <Center>
                        <op.icon />
                        <Box ml={10}>{capFirst(op.value)}</Box>
                      </Center>
                    ),
                  };
                })}
                styles={(theme) => ({
                  indicator: {
                    backgroundColor: getColourForSelectedAccountType(
                      selectedAccountType,
                      theme
                    ),
                  },
                })}
              />
              <FeatureResourcePreviewAccordion
                selectedAccountType={selectedAccountType}
                isMobile={true}
              />
            </Box>
            <Box h={65}>
              <FieldTextValue
                label="First Name"
                editing={true}
                formProps={form.getInputProps("firstName")}
              />
            </Box>

            <Box h={65}>
              <FieldTextValue
                label="Last Name"
                editing={true}
                formProps={form.getInputProps("lastName")}
              />
            </Box>

            <Box h={65}>
              <FieldTextValue
                label="Email"
                editing={true}
                formProps={form.getInputProps("email")}
              />
            </Box>
            <Box h={65}>
              <FieldPasswordValue
                label="Password"
                editing={true}
                formProps={form.getInputProps("password")}
              />
            </Box>
            <Box h={65}>
              <FieldPasswordValue
                label="Confirm Password"
                editing={true}
                formProps={form.getInputProps("confirmPassword")}
              />
            </Box>
            <FieldCheckboxValue
              formProps={form.getInputProps("termsandprivacy")}
              label={
                <Text c="dimmed">
                  I accept your{" "}
                  <Anchor href="/terms" target="_blank">
                    terms and conditions
                  </Anchor>{" "}
                  and{" "}
                  <Anchor href="/privacy" target="_blank">
                    privacy policy
                  </Anchor>
                </Text>
              }
            />
            <Button
              className={authClasses.ctaButton}
              variant="outline"
              color="darkblue"
              loading={loading}
              disabled={!form.isValid()}
              onClick={async () => await createUserAccount()}
            >
              {selectedAccountType === "Basic" ? "Create account" : "Next"}
            </Button>
          </Box>
        </div>
      </Stack>
      <AccountTypePreview selectedAccountType={selectedAccountType} />
    </Box>
  );
}

const options: { value: SelectedAccountType; icon: any }[] = [
  {
    value: "Basic",
    icon: ContactPageRounded,
  },
  {
    value: "Trial",
    icon: HourglassBottomRounded,
  },
  {
    value: "Member",
    icon: WhatshotRounded,
  },
];
